/**
 * Contagem própria de visitas às páginas públicas, sem cookie e sem consentimento: o navegador
 * manda só o caminho e, na primeira página da visita, de onde a pessoa veio. A API transforma
 * isso em totais do dia e não guarda IP nem navegador (ver `docs/ARCHITECTURE.md`, §5.5, “Visitas”).
 *
 * Não atrapalha o carregamento: espera a página terminar e o navegador ficar livre, e vai por
 * `sendBeacon`, que não segura a navegação.
 */

/** Só estas páginas contam. A área logada, convite e confirmação de e-mail ficam de fora. */
const PUBLIC_PAGES = [
  /^\/$/,
  /^\/moldes\/?$/,
  /^\/checkout\/[^/]+\/[^/]+\/?$/,
  /^\/cadastro\/?$/,
  /^\/login\/?$/,
];

export const isPublicPage = (pathname: string) => PUBLIC_PAGES.some((page) => page.test(pathname));

export interface VisitBody {
  path: string;
  referrer: string | null;
  source: string | null;
  entry: boolean;
}

export interface VisitTrackerOptions {
  /** URL da API (`/api`), igual à do `config.json`. */
  apiBaseUrl: string;
  hostname?: string;
  /** Espera a sessão voltar: operador não entra nos números. */
  ready: Promise<unknown>;
  isOperator: () => boolean;
  referrer?: string;
  /** Navegador automatizado (testes, Lighthouse) não conta. */
  automated?: boolean;
  send?: (url: string, body: string) => void;
  schedule?: (run: () => void) => void;
}

const LOCAL = ["localhost", "127.0.0.1", "[::1]"];

/** De fora do site, só o endereço do site de origem; do próprio site, nada. */
export function externalReferrer(referrer: string, hostname: string): string | null {
  try {
    const url = new URL(referrer);
    if (url.hostname === hostname || !/^https?:$/.test(url.protocol)) return null;
    return url.hostname;
  } catch {
    return null;
  }
}

/** Só o `utm_source`: o nome da campanha, nunca outro parâmetro da URL. */
export function campaignSource(search: string): string | null {
  const value = new URLSearchParams(search).get("utm_source")?.trim();
  return value ? value.slice(0, 40) : null;
}

export function createVisitTracker(options: VisitTrackerOptions) {
  const hostname = options.hostname ?? window.location.hostname;
  const automated = options.automated ?? navigator.webdriver === true;
  const off = LOCAL.includes(hostname) || automated;
  const url = `${options.apiBaseUrl.replace(/\/$/, "")}/metrics/visit`;
  const send = options.send ?? sendBeacon;
  const schedule = options.schedule ?? afterLoad;
  let first = true;

  return (location: { pathname: string; search: string }) => {
    if (off || !isPublicPage(location.pathname)) {
      first = false;
      return;
    }
    const entry = first;
    first = false;
    // A origem e a campanha só valem para a página em que a pessoa entrou.
    const body: VisitBody = {
      path: location.pathname,
      referrer: entry ? externalReferrer(options.referrer ?? document.referrer, hostname) : null,
      source: entry ? campaignSource(location.search) : null,
      entry,
    };
    schedule(() => {
      void options.ready.then(() => {
        if (!options.isOperator()) send(url, JSON.stringify(body));
      });
    });
  };
}

/** Texto puro não pede preflight e o beacon não espera resposta. */
function sendBeacon(url: string, body: string) {
  try {
    if (navigator.sendBeacon?.(url, body)) return;
  } catch {
    /* Sem beacon: tenta o fetch abaixo. */
  }
  void fetch(url, { method: "POST", body, keepalive: true, credentials: "omit" }).catch(() => {
    /* Visita não contada não é erro para quem navega. */
  });
}

function afterLoad(run: () => void) {
  const idle = () => {
    if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 4000 });
    else setTimeout(run, 1);
  };
  if (document.readyState === "complete") idle();
  else window.addEventListener("load", idle, { once: true });
}
